import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit"
import { apiClient } from "../../base_url/config"

// Payment Intent State
export interface PaymentIntent {
  orderId: string
  paymobOrderId: string
  paymentToken: string
  timestamp: string
}

// Payment Verification Response
export interface PaymentVerificationResponse {
  status: 'paid' | 'failed' | 'canceled'
  backendOrderId: string
  invoiceNo?: string
  message: string
}

/** Applied promotion amounts from order response. Display-only. */
export interface OrderPromotion {
  discount?: number | null
  cashback?: number | null
  set_item_price_savings?: number | null
  setItemPriceSavings?: number | null
}

export function hasDisplayableOrderPromotion(
  promotion: OrderPromotion | null | undefined,
): boolean {
  if (!promotion) return false
  const discount = promotion.discount ?? 0
  const cashback = promotion.cashback ?? 0
  const savings =
    promotion.set_item_price_savings ?? promotion.setItemPriceSavings ?? 0
  return discount > 0 || cashback > 0 || savings > 0
}

// Order Details Interface
export interface OrderDetails {
  id: string | number
  order_number: string
  customer_phone?: string
  payment_method?: string
  paymentMethod?: string
  items?: any[]
  total_price?: number
  totalPrice?: number
  subtotal?: number
  price?: number
  price_before_discount?: number
  applied_discount?: number
  tax_value?: number
  taxValue?: number
  created_at: string
  createdAt?: string
  status: string
  payment_status: string
  paymentStatus?: string
  discount?: number
  /** Backend-applied promotion benefits (display-only). */
  promotion?: OrderPromotion | null
  cobone?: any
  coboneCode?: string
  table?: {
    id: number
    name: string
    seats_number: number
    seatsNumber?: number
    table_type: string
    tableType?: string
    table_price: string
    tablePrice?: string
  }
  table_price?: string
  tablePrice?: string
  delivery_price?: number
  deliveryPrice?: number
  tax_percentage?: string
  taxPercentage?: string
  user?: {
    name: string
    phone: string
  }
  branch?: string | {
    id: number
    name: string
    address: string
  }
  type_id?: number
  typeId?: number
  platform?: string
  notes?: string
}

// Payment State Interface
export interface PaymentState {
  intent?: PaymentIntent
  verifying: boolean
  verified?: 'paid' | 'failed' | 'canceled'
  lastError?: string
  order?: OrderDetails
  loading: boolean
}

const initialState: PaymentState = {
  intent: undefined,
  verifying: false,
  verified: undefined,
  lastError: undefined,
  order: undefined,
  loading: false,
}

// Async thunk for creating order intent
export const createOrderIntent = createAsyncThunk(
  "payment/createOrderIntent",
  async (
    { orderData, token, lang }: {
      orderData: any
      token: string
      lang: string
    },
    { rejectWithValue }
  ) => {
    try {
      // Validate input data
      if (!orderData || !token) {
        return rejectWithValue("Missing required data for order creation")
      }

      if (!orderData.cartId) {
        return rejectWithValue("Cart ID is required")
      }

      if (!orderData.branchId || !orderData.typeId) {
        return rejectWithValue("Branch ID and Type ID are required")
      }

      // Step 1: Create Order
      const orderResponse = await apiClient.post("/qr/orders", orderData, {
        headers: {
          lang: lang,
        },
      })

      const orderId = orderResponse.data.data.order.id

      // Validate order creation response
      if (!orderId) {
        return rejectWithValue("Order creation failed - no order ID returned")
      }

      // Step 2: Create Payment
      const paymentResponse = await apiClient.post(
        "/qr/paymob/createPayment",
        {
          type: "order",
          order_id: orderId,
          platform: "qr"
        },
        {
          headers: {
            lang: lang,
          },
        }
      )

      const paymentToken = paymentResponse.data.data.client_secret

      // Validate payment creation response
      if (!paymentToken) {
        return rejectWithValue("Payment creation failed - no payment token returned")
      }

      // Generate a unique Paymob order ID (this would normally come from Paymob)
      const paymobOrderId = `pm_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

      // Create enhanced payment token that includes order_id
      const enhancedPaymentToken = {
        token: paymentToken,
        order_id: orderId,
        timestamp: new Date().toISOString()
      }

      // Build Paymob URL
      const paymobPublicKey = process.env.REACT_APP_PAYMOB_PUBLIC_KEY
      if (!paymobPublicKey) {
        return rejectWithValue('Paymob public key not found in environment variables')
      }
      
      const returnUrl = `${window.location.origin}/payment/return`
      const paymobUrl = `https://ksa.paymob.com/unifiedcheckout/?publicKey=${encodeURIComponent(paymobPublicKey)}&clientSecret=${paymentToken}&return_url=${encodeURIComponent(returnUrl)}`

      const intent: PaymentIntent = {
        orderId: orderId.toString(),
        paymobOrderId,
        paymentToken: JSON.stringify(enhancedPaymentToken), // Store enhanced token
        timestamp: new Date().toISOString(),
      }

      // Store in sessionStorage for persistence
      sessionStorage.setItem('pay_intent', JSON.stringify(intent))
      sessionStorage.setItem('pay_state', 'pending')
      sessionStorage.setItem('paymob_url', paymobUrl) // Store the Paymob URL for retry
      
      // Also store in a more accessible location for retry
      sessionStorage.setItem('last_paymob_url', paymobUrl)
      
      // Payment data stored successfully

      return intent
    } catch (error: any) {
      // Log error for debugging
      console.error('createOrderIntent error:', error)
      
      let errorMessage = "Failed to create order intent"
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      } else if (error.code === 'NETWORK_ERROR' || error.message === 'Network Error') {
        errorMessage = "Network error - please check your connection"
      } else if (error.response?.status === 401) {
        errorMessage = "Authentication failed - please login again"
      } else if (error.response?.status === 403) {
        // Check if it's a table availability issue
        if (error.response?.data?.descriptiveMessage?.includes('table') || 
            error.response?.data?.descriptiveMessage?.includes('session_not_belong_to_user')) {
          errorMessage = "الطاولة محجوزة من قبل مستخدم آخر - يرجى تغيير الطاولة"
        } else {
          errorMessage = "Access forbidden - session may have expired, please login again"
        }
      } else if (error.response?.data?.descriptiveMessage === 'has_no_open_session') {
        errorMessage = "انتهت صلاحية الجلسة - يرجى تسجيل الدخول مرة أخرى"
      } else if (error.response?.status === 400) {
        errorMessage = "Invalid request - please check your order details"
      } else if (error.response?.status >= 500) {
        errorMessage = "Server error - please try again later"
      }
      
      return rejectWithValue(errorMessage)
    }
  }
)

// Async thunk for verifying payment
export const verifyPayment = createAsyncThunk(
  "payment/verifyPayment",
  async (
    { params, token, lang }: {
      params: {
        success?: string
        paymob_order_id?: string
        merchant_order_id?: string
        amount_cents?: string
        hmac?: string
        [key: string]: any
      }
      token: string
      lang: string
    },
    { rejectWithValue }
  ) => {
    try {
      // Call backend to verify payment with HMAC
      const response = await apiClient.post(
        "/qr/payments/paymob/verify",
        params,
        {
          headers: {
            lang: lang,
          },
        }
      )

      // If backend verification fails, throw error
      if (!response.data.success) {
        throw new Error(response.data.message || 'Payment verification failed')
      }

      const result: PaymentVerificationResponse = {
        status: response.data.data.status,
        backendOrderId: response.data.data.backendOrderId,
        invoiceNo: response.data.data.invoiceNo,
        message: response.data.message,
      }

      // Update sessionStorage based on verification result
      if (result.status === 'paid') {
        sessionStorage.setItem('pay_state', 'paid')
        sessionStorage.removeItem('pay_intent')
      } else {
        sessionStorage.setItem('pay_state', 'failed')
      }

      return result
    } catch (error: any) {
      // Set payment state to failed on error
      sessionStorage.setItem('pay_state', 'failed')
      
      const errorMessage = error.response?.data?.message || error.message || "Payment verification failed"
      return rejectWithValue(errorMessage)
    }
  }
)

// Async thunk for fetching order details
export const fetchOrderDetails = createAsyncThunk(
  "payment/fetchOrderDetails",
  async (
    { orderId, token, lang }: {
      orderId: string
      token: string
      lang: string
    },
    { rejectWithValue }
  ) => {
    try {
      // Fetching order details

      // محاولة أولى: endpoint الطلبات
      let response;
      try {
        response = await apiClient.get(`/qr/orders/${orderId}`, {
          headers: {
            lang: lang,
          },
        });
        // Order details fetched successfully from /qr/orders endpoint
      } catch (firstError: any) {
        // First endpoint failed, trying alternative
        
        // محاولة ثانية: endpoint القوائم مع orderId
        try {
          response = await apiClient.get(`/qr/menus/branches/1?typeId=1&orderId=${orderId}`, {
            headers: {
              lang: lang,
            },
          });
          // Order details fetched from alternative endpoint
        } catch (secondError: any) {
          // Both endpoints failed
          
          // إذا كان الخطأ 404 أو 400، فهذا يعني أن الطلب غير موجود بعد
          // لا نعتبر هذا خطأ حقيقي، بل نترك البيانات المحفوظة تظهر
          if (firstError.response?.status === 404 || firstError.response?.status === 400) {
            // Order not found in API yet, this is normal for new orders
            throw new Error('ORDER_NOT_FOUND_YET');
          }
          
          throw firstError; // نرمي الخطأ الأول
        }
      }

      const orderDetails: OrderDetails = response.data.data || response.data;

      // Clear payment-related session storage on successful order fetch
      if (orderDetails.payment_status === 'paid') {
        sessionStorage.removeItem('pay_intent')
        sessionStorage.removeItem('pay_state')
        sessionStorage.removeItem('orderToken')
      }

      return orderDetails
    } catch (error: any) {
      // Error fetching order details
      
      const errorMessage = error.response?.data?.message || error.message || "Failed to fetch order details"
      return rejectWithValue(errorMessage)
    }
  }
)

const paymentSlice = createSlice({
  name: "payment",
  initialState,
  reducers: {
    clearPaymentState: (state) => {
      state.intent = undefined
      state.verifying = false
      state.verified = undefined
      state.lastError = undefined
      state.order = undefined
      state.loading = false
    },
    setPaymentError: (state, action: PayloadAction<string>) => {
      state.lastError = action.payload
    },
    clearPaymentError: (state) => {
      state.lastError = undefined
    },
    restorePaymentIntent: (state) => {
      try {
        const storedIntent = sessionStorage.getItem('pay_intent')
        if (storedIntent) {
          state.intent = JSON.parse(storedIntent)
        }
      } catch (error) {
        // Silently fail if stored data is corrupted
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // createOrderIntent
      .addCase(createOrderIntent.pending, (state) => {
        state.loading = true
        state.lastError = undefined
      })
      .addCase(createOrderIntent.fulfilled, (state, action) => {
        state.loading = false
        state.intent = action.payload
        state.lastError = undefined
      })
      .addCase(createOrderIntent.rejected, (state, action) => {
        state.loading = false
        state.lastError = action.payload as string
      })
      // verifyPayment
      .addCase(verifyPayment.pending, (state) => {
        state.verifying = true
        state.lastError = undefined
      })
      .addCase(verifyPayment.fulfilled, (state, action) => {
        state.verifying = false
        state.verified = action.payload.status
        state.lastError = undefined
      })
      .addCase(verifyPayment.rejected, (state, action) => {
        state.verifying = false
        state.verified = 'failed'
        state.lastError = action.payload as string
      })
      // fetchOrderDetails
      .addCase(fetchOrderDetails.pending, (state) => {
        state.loading = true
        state.lastError = undefined
      })
      .addCase(fetchOrderDetails.fulfilled, (state, action) => {
        state.loading = false
        state.order = action.payload
        state.lastError = undefined
      })
      .addCase(fetchOrderDetails.rejected, (state, action) => {
        state.loading = false
        state.lastError = action.payload as string
      })
  },
})

export const {
  clearPaymentState,
  setPaymentError,
  clearPaymentError,
  restorePaymentIntent,
} = paymentSlice.actions

// Selectors
export const selectPayment = (state: { payment: PaymentState }) => state.payment
export const selectPaymentLoading = (state: { payment: PaymentState }) => state.payment.loading
export const selectPaymentVerifying = (state: { payment: PaymentState }) => state.payment.verifying
export const selectPaymentVerified = (state: { payment: PaymentState }) => state.payment.verified
export const selectPaymentError = (state: { payment: PaymentState }) => state.payment.lastError
export const selectPaymentIntent = (state: { payment: PaymentState }) => state.payment.intent
export const selectPaymentOrder = (state: { payment: PaymentState }) => state.payment.order

export default paymentSlice.reducer
